"use client";

import { c as _c } from "react/compiler-runtime";
import { DateTime } from "luxon";
import { Badge } from "@/src/components/shadcn/ui/badge";
import type { FhirOrder } from "@/src/lib/fhir-types";
import type { OrderMatchOutput } from "@/src/lib/workflows/steps/ehr/order-match";
function formatDate(dateString: string | null): string | null {
  if (!dateString) return null;

  // Date-only strings: split directly to avoid UTC midnight shifting to previous day
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split("-");
    return `${month}/${day}/${year}`;
  }

  // Full ISO timestamps: read in UTC so the calendar date matches what was stored
  const dt = DateTime.fromISO(dateString, {
    zone: "utc"
  });
  return dt.isValid ? dt.toFormat("MM/dd/yyyy") : dateString;
}
interface OrderMatchPanelProps {
  match: OrderMatchOutput | undefined;
}
export function OrderMatchPanelView(t0) {
  const $ = _c(26);
  const {
    match
  } = t0;
  if (!match) {
    return null;
  }
  if (match.matchedOrder) {
    let t1;
    if ($[0] !== match.labContext) {
      t1 = <DocumentContextSection labContext={match.labContext} />;
      $[0] = match.labContext;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <p><strong>Matched Order (verify before completing):</strong></p>;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== match.matchedOrder) {
      t3 = <OrderSummaryRow order={match.matchedOrder} />;
      $[3] = match.matchedOrder;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== t1 || $[6] !== t3) {
      t4 = <div className="flex flex-col gap-2 mt-2">{t1}{t2}{t3}</div>;
      $[5] = t1;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t1;
  if ($[8] !== match.candidateOrders) {
    t1 = match.candidateOrders ?? [];
    $[8] = match.candidateOrders;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const candidates = t1;
  if (candidates.length === 0) {
    let t2;
    if ($[10] !== match.labContext) {
      t2 = <DocumentContextSection labContext={match.labContext} />;
      $[10] = match.labContext;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    let t3;
    let t4;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <p><strong>Candidate Orders:</strong></p>;
      t4 = <p className="text-muted-foreground italic">None in the matching date window.</p>;
      $[12] = t3;
      $[13] = t4;
    } else {
      t3 = $[12];
      t4 = $[13];
    }
    let t5;
    if ($[14] !== t2) {
      t5 = <div className="flex flex-col gap-2 mt-2">{t2}{t3}{t4}</div>;
      $[14] = t2;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    return t5;
  }
  let t2;
  if ($[16] !== match.labContext) {
    t2 = <DocumentContextSection labContext={match.labContext} />;
    $[16] = match.labContext;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  let t3;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p><strong>Candidate Orders (verify none match):</strong></p>;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== candidates) {
    t4 = candidates.map(_temp);
    $[19] = candidates;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== t4) {
    t5 = <div className="flex flex-col gap-3">{t4}</div>;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== t2 || $[24] !== t5) {
    t6 = <div className="flex flex-col gap-2 mt-2">{t2}{t3}{t5}</div>;
    $[23] = t2;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return t6;
}
function _temp(candidate, idx) {
  return <OrderSummaryRow key={candidate.id} order={candidate} index={idx + 1} />;
}
function DocumentContextSection(t0) {
  const $ = _c(14);
  const {
    labContext
  } = t0;
  if (!labContext) {
    return null;
  }
  let t1;
  if ($[0] !== labContext.labNames) {
    t1 = labContext.labNames.length > 0 ? labContext.labNames.join(", ") : null;
    $[0] = labContext.labNames;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      label: "Test name",
      value: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== labContext.dateOfEvent) {
    t3 = formatDate(labContext.dateOfEvent);
    $[4] = labContext.dateOfEvent;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = {
      label: "Date of event",
      value: t3
    };
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    const lines = [t2, t4];
    t5 = lines.filter(_temp2);
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const visibleLines = t5;
  if (visibleLines.length === 0) {
    return null;
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p><strong>Document:</strong></p>;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== visibleLines) {
    t7 = <div className="flex flex-col gap-1 mb-1">{t6}<div className="pl-4 border-l-2 border-blue-500/30">{visibleLines.map(_temp3)}</div></div>;
    $[12] = visibleLines;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
function _temp3(t0) {
  const {
    label,
    value: value_0
  } = t0;
  return <p key={label} className="whitespace-pre-line"><strong>{label}:</strong> {value_0}</p>;
}
function _temp2(t0) {
  const {
    value
  } = t0;
  return value;
}
function OrderSummaryRow(t0) {
  const $ = _c(26);
  const {
    order,
    index
  } = t0;
  let t1;
  if ($[0] !== order.orderName) {
    t1 = {
      label: "Order",
      value: order.orderName
    };
    $[0] = order.orderName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== order.orderDate) {
    t2 = formatDate(order.orderDate);
    $[2] = order.orderDate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = {
      label: "Order date",
      value: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== order.collectionDate) {
    t4 = formatDate(order.collectionDate);
    $[6] = order.collectionDate;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = {
      label: "Collection date",
      value: t4
    };
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t1 || $[11] !== t3 || $[12] !== t5) {
    t6 = [t1, t3, t5];
    $[10] = t1;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const lines = t6;
  let t7;
  if ($[14] !== index || $[15] !== order.ecwStatus) {
    t7 = index != null && <p className="flex items-center gap-2"><strong>#{index}</strong>{order.ecwStatus && <Badge variant="secondary">{order.ecwStatus}</Badge>}</p>;
    $[14] = index;
    $[15] = order.ecwStatus;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== index || $[18] !== order.ecwStatus) {
    t8 = index == null && order.ecwStatus && <p><Badge variant="secondary">{order.ecwStatus}</Badge></p>;
    $[17] = index;
    $[18] = order.ecwStatus;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== lines) {
    t9 = lines.map(_temp4);
    $[20] = lines;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t10 = <div className="pl-4 border-l-2 border-green-500/30">{t7}{t8}{t9}</div>;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}
function _temp4(t0) {
  const {
    label,
    value
  } = t0;
  return value ? <p key={label} className="whitespace-pre-line"><strong>{label}:</strong> {value}</p> : null;
}